import React from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { getCategoryTheme } from "@/lib/categoryTheme";

interface CategoryBadgeProps {
  category: string;
  label?: string;
  showIcon?: boolean;
  iconClassName?: string;
  variant?: "solid" | "subtle" | "glass" | "outline";
  size?: "xs" | "sm" | "md" | "lg";
  asLink?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function CategoryBadge({
  category,
  label,
  showIcon = true,
  iconClassName,
  variant = "solid",
  size = "sm",
  asLink = false,
  className,
  style,
}: CategoryBadgeProps) {
  const theme = getCategoryTheme(category);
  const displayLabel = label || theme.name;
  const Icon = theme.Icon;

  const sizeClasses = {
    xs: "text-[10px] px-2 py-0.5 gap-1 rounded-md",
    sm: "text-[11px] px-2.5 py-0.5 gap-1.5 rounded-lg",
    md: "text-xs px-3 py-1 gap-1.5 rounded-xl font-semibold",
    lg: "text-sm px-4 py-1.5 gap-2 rounded-2xl font-bold",
  }[size];

  const iconSizes = {
    xs: "h-2.5 w-2.5",
    sm: "h-3 w-3",
    md: "h-3.5 w-3.5",
    lg: "h-4 w-4",
  }[size];

  let variantClasses = "";
  let dynamicStyle: React.CSSProperties = { ...style };

  if (variant === "solid") {
    variantClasses = "text-white shadow-sm font-bold tracking-wide transition-all duration-300";
    dynamicStyle = {
      background: theme.badgeGradient,
      ...dynamicStyle,
    };
  } else if (variant === "subtle") {
    variantClasses = "font-semibold transition-all duration-300 border border-transparent";
    dynamicStyle = {
      backgroundColor: theme.subtleBg,
      color: theme.color,
      borderColor: `${theme.color}30`,
      ...dynamicStyle,
    };
  } else if (variant === "glass") {
    variantClasses = "text-white backdrop-blur-md border border-white/20 font-bold shadow-sm";
    dynamicStyle = {
      background: `linear-gradient(135deg, ${theme.gradientFrom}cc, ${theme.gradientTo}cc)`,
      ...dynamicStyle,
    };
  } else if (variant === "outline") {
    variantClasses = "bg-transparent font-semibold border";
    dynamicStyle = {
      borderColor: theme.color,
      color: theme.color,
      ...dynamicStyle,
    };
  }

  const content = (
    <span
      style={dynamicStyle}
      className={cn(
        "inline-flex items-center select-none uppercase tracking-wider shrink-0 transition-transform duration-200",
        sizeClasses,
        variantClasses,
        asLink && "hover:scale-[1.03] hover:shadow-md cursor-pointer",
        className,
      )}
    >
      {showIcon && <Icon className={cn(iconSizes, "shrink-0", iconClassName)} aria-hidden="true" />}
      <span className="truncate">{displayLabel}</span>
    </span>
  );

  if (asLink) {
    return (
      <Link
        to={`/category/${theme.slug}`}
        className="inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
        aria-label={`View more ${displayLabel} articles`}
      >
        {content}
      </Link>
    );
  }

  return content;
}
